import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import '../components/landing.css';
import './getting-started.css';

const APP_STORE_URL = 'https://apps.apple.com/sa/app/paddiq/id6785660120';
const PAGE_TITLE = 'Analyze laps and turns on iPhone | Paddiq';

export function IPhoneSessionAnalysisPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">iPhone session analysis guide</p>
            <h1>Analyze laps and turns on iPhone.</h1>
            <p className="guide-hero__lead">
              Choose a lap, add a comparison, and review speed traces, turn speeds, braking zones,
              and estimated coasting from the session map.
            </p>
            <div className="guide-hero__actions">
              <a className="landing-btn landing-btn--primary landing-btn--lg" href={APP_STORE_URL}>
                Download on the App Store
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/compare-laps-and-traces"
              >
                Use the web guide
              </a>
            </div>
            <p className="guide-hero__meta">iPhone guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/analyze-sessions-iphone" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#choose-lap">Choose a lap</a>
                </li>
                <li>
                  <a href="#compare">Add a comparison</a>
                </li>
                <li>
                  <a href="#traces">Review lap traces</a>
                </li>
                <li>
                  <a href="#turns">Check turn speeds</a>
                </li>
                <li>
                  <a href="#overlays">Review deceleration</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>
                  Choose an uploaded session with a selectable lap. GPS and recorded telemetry are
                  needed for the map and traces.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="choose-lap">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Session and lap</p>
                  <h2>Start with one clean lap.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Sessions</strong>, then tap the session name above the map to
                      open <strong>Session &amp; laps</strong>.
                    </li>
                    <li>Choose the session you want to review.</li>
                    <li>
                      Use <strong>Circuit Laps</strong> for laps built from the saved circuit, or{' '}
                      <strong>Raw laps</strong> for the logger&apos;s recorded lap beacons.
                    </li>
                    <li>Choose the primary lap, then close the panel.</li>
                  </ol>
                  <div className="guide-callout">
                    Start with a complete flying lap. Out, In, and Crash rows stay visible for
                    context but cannot be selected as the primary lap.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="compare">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Comparison</p>
                  <h2>Add a second lap with a clear purpose.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Session &amp; laps</strong> and tap{' '}
                      <strong>Add comparison lap</strong>.
                    </li>
                    <li>
                      Under <strong>Compare session 1</strong>, keep the same run or choose another
                      uploaded session.
                    </li>
                    <li>
                      Under <strong>Compare lap</strong>, choose the lap you want to compare.
                    </li>
                    <li>Close the panel and confirm both lap rows appear above the map.</li>
                  </ol>
                  <p>
                    For the clearest comparison, use laps from the same circuit configuration and
                    keep both in the same Circuit Laps or Raw laps mode.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="traces">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Recorded data</p>
                  <h2>Open Lap traces and follow the replay.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Tap <strong>Lap traces</strong> on the right side of the map.
                    </li>
                    <li>
                      Tap <strong>Charts</strong> if the panel opens in compact mode.
                    </li>
                    <li>
                      Drag the replay control, tap a point on a trace, or press play to move both
                      laps through the same elapsed time.
                    </li>
                    <li>
                      Tap <strong>Add traces</strong> to choose another recorded channel, then tap{' '}
                      <strong>Done</strong>. Use <strong>Compact</strong> when you want more map
                      room.
                    </li>
                  </ol>
                  <p>
                    Start with speed. When a comparison is active, Speed Δ is comparison speed minus
                    primary speed at that moment; it is not a lap-time difference.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="turns">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Corner review</p>
                  <h2>Read Entry, Apex, and Exit together.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Tap <strong>Turns</strong> on the right side of the map.
                    </li>
                    <li>Find the turn you want to study.</li>
                    <li>
                      Compare its <strong>Entry</strong>, <strong>Apex</strong>, and{' '}
                      <strong>Exit</strong> speeds instead of judging one value alone.
                    </li>
                    <li>Return to Lap traces to see what happened before and after the turn.</li>
                  </ol>
                  <p>
                    Turn speeds are available when the selected circuit includes turn boundaries. A
                    dash means Paddiq could not calculate that phase from the selected lap.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="overlays">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Map overlays</p>
                  <h2>Find sections worth a closer look.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Tap <strong>Overlays</strong> on the right side of the map.
                    </li>
                    <li>
                      Turn on <strong>Braking zones</strong> to show sustained soft and hard
                      deceleration.
                    </li>
                    <li>
                      Turn on <strong>Estimated coast</strong> to show sustained mild deceleration.
                    </li>
                    <li>
                      Check the same section in Lap traces and Turns before drawing a conclusion.
                    </li>
                  </ol>
                </div>
              </section>

              <figure className="guide-workflow guide-workflow--mobile-panel">
                <img
                  src="/iphone-session-map-overlays.webp"
                  width="568"
                  height="410"
                  alt="Paddiq iPhone Map overlays panel with Braking zones on and Estimated coast off"
                />
                <figcaption>
                  Overlays can highlight where recorded speed drops. They estimate deceleration from
                  GPS speed and do not prove when the brake or throttle was used.
                </figcaption>
              </figure>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If an analysis control is unavailable</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Lap traces is unavailable</h3>
                    <p>
                      Choose a primary session and selectable primary lap. If traces still do not
                      open, try a session with complete recorded telemetry.
                    </p>
                  </article>
                  <article>
                    <h3>Circuit Laps are missing</h3>
                    <p>
                      Use <strong>Raw laps</strong>, or open <strong>Circuit setup</strong> to
                      choose the intended circuit and rebuild its laps.
                    </p>
                  </article>
                  <article>
                    <h3>Turns has no speeds</h3>
                    <p>
                      Confirm that the selected circuit includes turn boundaries and that the lap
                      passes through them with usable GPS-speed data.
                    </p>
                  </article>
                  <article>
                    <h3>Overlays show no colored sections</h3>
                    <p>
                      The selected data may not contain enough sustained deceleration. Try another
                      complete lap; an empty overlay does not prove the driver never braked or
                      coasted.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Keep the review focused</p>
                  <h2>Start with two laps and one corner.</h2>
                  <p>
                    Use the traces, turn speeds, and map overlays together to test one clear
                    question before moving to the next section of the lap.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href="/resources/import-mychron-iphone"
                  >
                    Import a session
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
